import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";
import { LotusDivider } from "../Decorative";
import { LOGO_URL, ANTARA_IG, KASHI_IG, KASHI_SITE, PERFORM } from "../../lib/constants";
import { useUpcomingShows, fullDate } from "../../lib/shows";
import { EVENT_TITLE } from "../../data/shows";
import { NAV, ROUTES } from "../../lib/seo";
import { trackPerformClick } from "../../lib/track";
import { EDITIONS } from "../../lib/editions";

const LATEST = EDITIONS[0];

export default function Footer() {
  const shows = useUpcomingShows();
  return (
    <footer className="bg-[#0F0A07] pt-20 pb-10 px-6 relative overflow-hidden" data-testid="footer">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="Antara" width="56" height="56" className="h-14 w-14 object-contain" />
          <p className="mt-3 font-serif-italic text-sm" style={{ color: "#C9A961" }}>
            Presented by{" "}
            <a href={KASHI_SITE} target="_blank" rel="noopener" className="underline-offset-4 hover:underline transition" style={{ color: "#E5C880" }} data-testid="footer-kashi-link">
              Kashi with Anshu
            </a>
          </p>
          {/* The next shows, not the last one. While a show is ahead this is
              the only date on most pages, and pointing it at an evening that
              has already happened reads as though nothing is coming. */}
          {shows.length ? (
            <div className="mt-4 font-body text-sm space-y-1" style={{ color: "#E8D9C5" }} data-testid="footer-shows">
              <p style={{ color: "#E5C880" }}>{EVENT_TITLE}</p>
              {shows.map((s) => (
                <p key={s.id}>
                  <a href={s.bookingUrl} target="_blank" rel="noopener noreferrer" className="hover:underline underline-offset-4">
                    {s.city} · {fullDate(s)} · {s.venue}
                  </a>
                </p>
              ))}
            </div>
          ) : (
            <p className="mt-4 font-body text-sm" style={{ color: "#E8D9C5" }} data-testid="footer-latest">
              <Link to={`${ROUTES.editions.path}${LATEST.slug}/`} className="hover:underline underline-offset-4">
                Latest: {LATEST.title ? `${LATEST.title} · ` : ""}{LATEST.dateLabel} · {LATEST.venue}, {LATEST.city}
              </Link>
            </p>
          )}
        </div>

        <LotusDivider />

        {/* Sitemap links */}
        <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 max-w-3xl mx-auto" aria-label="Footer">
          {NAV.map((r) => (
            <Link key={r.path} to={r.path} className="font-body text-[13px] tracking-wide transition-colors" style={{ color: "#B8A788" }}>
              {r.crumb}
            </Link>
          ))}
          {[ROUTES.host, ROUTES.sponsorship, ROUTES.chandigarh, ROUTES.gurgaon].map((r) => (
            <Link key={r.path} to={r.path} className="font-body text-[13px] tracking-wide transition-colors" style={{ color: "#E5C880" }} data-testid={`footer-${r.path.replace(/\//g, "")}-link`}>
              {r.crumb}
            </Link>
          ))}
          {PERFORM.open && (
            <Link
              to={ROUTES.perform.path}
              onClick={trackPerformClick}
              className="font-body text-[13px] tracking-wide transition-colors"
              style={{ color: "#E5C880" }}
              data-testid="footer-perform-link"
            >
              Perform at Antara
            </Link>
          )}
        </nav>

        <div className="mt-12 flex flex-col items-center gap-5">
          <div className="flex items-center gap-5">
            <a href={ANTARA_IG} target="_blank" rel="noopener noreferrer" aria-label="Antara Instagram" className="w-11 h-11 rounded-full border flex items-center justify-center transition" style={{ borderColor: "rgba(201,169,97,0.4)", color: "#C9A961" }} data-testid="footer-antara-ig">
              <Instagram size={18} strokeWidth={1.3} />
            </a>
            <a href={KASHI_IG} target="_blank" rel="noopener noreferrer" aria-label="Kashi with Anshu Instagram" className="w-11 h-11 rounded-full border flex items-center justify-center transition" style={{ borderColor: "rgba(201,169,97,0.4)", color: "#C9A961" }} data-testid="footer-kashi-ig">
              <Instagram size={18} strokeWidth={1.3} />
            </a>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 font-body text-sm" style={{ color: "#E8D9C5" }}>
            <a href="mailto:hello@kashiwithanshu.com" className="flex items-center gap-2 hover:opacity-80 transition"><Mail size={14} />hello@kashiwithanshu.com</a>
            <a href="tel:+919355322133" className="flex items-center gap-2 hover:opacity-80 transition"><Phone size={14} />+91 93553 22133</a>
          </div>
        </div>
      </div>

      {/* Ganga ghat silhouette strip */}
      <div className="mt-14 relative h-20 overflow-hidden">
        <svg viewBox="0 0 1200 80" className="absolute bottom-0 left-0 right-0 w-full h-full" preserveAspectRatio="none" aria-hidden>
          <path d="M0,80 L0,52 L40,55 L60,38 L70,48 L100,42 L130,30 L160,35 L180,22 L210,28 L240,40 L280,32 L320,38 L350,25 L380,30 L410,18 L450,28 L490,35 L520,30 L560,40 L600,22 L640,28 L680,35 L720,28 L760,38 L800,30 L830,22 L870,30 L910,38 L950,30 L990,35 L1030,28 L1070,32 L1100,40 L1140,32 L1170,38 L1200,30 L1200,80 Z" fill="#9B8B7A" opacity="0.3" />
          <path d="M0,80 L0,62 L60,58 L120,68 L200,58 L280,65 L360,55 L440,62 L520,55 L600,65 L680,58 L760,62 L840,55 L920,65 L1000,58 L1080,62 L1160,58 L1200,65 L1200,80 Z" fill="#9B8B7A" opacity="0.5" />
        </svg>
      </div>
      <p className="text-center mt-4 font-body text-xs tracking-wide" style={{ color: "rgba(232,217,197,0.55)" }}>
        © 2026 Antara · Curated by Kashi with Anshu · bhajanjamming.com
      </p>
    </footer>
  );
}
